import {
    ChangeEvent,
    Dispatch,
    RefObject,
    SetStateAction,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';

import {
    Button,
    ChevronDownIcon,
    Controller,
    RadioButtonGroup,
} from '@selina-finance/ui';
import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { ApplicationContext } from '@src/app/contexts/Application';
import { DebtConContext } from '@src/app/contexts/DebtCon';
import {
    CategoryPortal,
    CreditCommitmentsData,
    Declaration,
} from '@src/app/interfaces/debtcon';
import {
    getFormItems,
    isCreditCardOrMailOrder,
} from '@src/shared/helpers/debtCon';
import {
    getDefaultValues,
    getFormHelpers,
    getValidationSchema,
    radioButtonOptions,
} from '@src/shared/helpers/input';
import { HttpError } from '@src/ui/components/HttpError';
import Tick3DIcon from '@src/ui/components/Icons/Tick3DIcon';
import SelectBox from '@src/ui/components/SelectBox/SelectBox';
import ToggleSwitch from '@src/ui/components/ToggleSwitch/ToggleSwitch';

import CommonQuestions from './CommonQuestions';
import CreditCardMailOrderForm from './CreditCardMailOrderForm';
import { handleDebtTypeChange } from './NewDebtInputView';

interface IDebtInputFormProps {
    currentDebt: CreditCommitmentsData;
    currentDebtIndex: number;
    setCurrentDebtIndex: Dispatch<SetStateAction<number>>;
    topRef: RefObject<HTMLDivElement>;
    debts: CreditCommitmentsData[];
}

const DebtInputForm = ({
    currentDebt,
    currentDebtIndex,
    setCurrentDebtIndex,
    debts,
    topRef,
}: IDebtInputFormProps) => {
    const navigate = useNavigate();
    const { setCurrentForm, applicants } = useContext(ApplicationContext);
    const {
        setCreditCommitmentsData,
        updateCreditCommitment,
        setToggleStates,
        toggleStates,
        reviewPageNav,
    } = useContext(DebtConContext);

    const formikRef = useRef<any>(null);
    const [error, setError] = useState<string | null>(null);

    const [_, setCategory] = useState<CategoryPortal>(
        currentDebt.categoryPortal,
    );

    const formItems = getFormItems(
        currentDebt,
        applicants,
        isCreditCardOrMailOrder(currentDebt.type),
    );

    const defaultValues = getDefaultValues(formItems);

    const validationSchema = Yup.lazy((values: any) => {
        const items = getFormItems(
            values,
            applicants,
            isCreditCardOrMailOrder(values?.type),
        );
        return getValidationSchema(items);
    });

    useEffect(() => {
        formikRef.current.values = {
            ...defaultValues,
            ...currentDebt,
        };
    }, [defaultValues, currentDebt]);

    useEffect(() => {
        if (formikRef.current) {
            setCurrentForm(formikRef.current);
        } else {
            setCurrentForm(undefined);
        }
    }, [formikRef, setCurrentForm]);

    const saveDebt = async (values: CreditCommitmentsData) => {
        const response = await updateCreditCommitment(values);
        if (!response?.success) {
            setError(response.error);
        } else {
            setCreditCommitmentsData((prevDebts: CreditCommitmentsData[]) => {
                const updatedDebts = prevDebts.map((debt, index) =>
                    index === currentDebtIndex ? { ...debt, ...values } : debt,
                );

                if (toggleStates) {
                    setToggleStates(
                        (prevToggleStates: Record<string, boolean> | null) => {
                            const updatedToggleStates = {
                                ...(prevToggleStates || {}),
                            };
                            updatedDebts.forEach((debt) => {
                                if (debt.dffDebtConSkipped) {
                                    updatedToggleStates[debt.id.toString()] =
                                        false;
                                }
                            });
                            return updatedToggleStates;
                        },
                    );
                }

                return updatedDebts;
            });

            if (error) {
                setError(null);
            }

            nextStep();
        }
    };

    const nextStep = () => {
        if (reviewPageNav || currentDebtIndex === debts.length - 1) {
            navigate('/eligibility/debt-con/review');
        } else {
            setCurrentDebtIndex((prev) => prev + 1);
            topRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const showSecurityPropertyQuestion =
        currentDebt.categoryPortal === CategoryPortal.MortgagesAndSecured &&
        currentDebt.declaration !== Declaration.User;

    return (
        <Formik
            enableReinitialize
            initialValues={{ ...defaultValues, ...currentDebt }}
            validationSchema={validationSchema}
            onSubmit={(values) => {
                saveDebt({ ...values, dffDebtConSkipped: false });
            }}
            innerRef={(ref) => (formikRef.current = ref)}
        >
            {(formikProps) => {
                const creditCardOrMailOrder = isCreditCardOrMailOrder(
                    formikProps.values.type,
                );

                const liveFormItems = getFormItems(
                    formikProps.values,
                    applicants,
                    creditCardOrMailOrder,
                );
                const { getError } = getFormHelpers(liveFormItems, formikProps);

                const sharedProps = {
                    formItems: liveFormItems,
                    values: formikProps.values,
                    setFieldValue: formikProps.setFieldValue,
                    handleChange: formikProps.handleChange,
                    handleBlur: formikProps.handleBlur,
                    getError,
                };

                return (
                    <Form className='lg:w-1/2'>
                        {currentDebt.declaration !== Declaration.User && (
                            <div className='bg-almond-100 p-2 flex justify-between items-center rounded'>
                                <p className='text-sm font-bold text-midnight-200'>
                                    I've paid off this debt
                                </p>
                                <ToggleSwitch
                                    checked={formikProps.values.ignore || false}
                                    onChange={(checked: boolean) => {
                                        formikProps.setFieldValue(
                                            'ignore',
                                            checked,
                                        );
                                    }}
                                />
                            </div>
                        )}
                        {formikProps.values.ignore ? (
                            <div className='flex justify-center mt-14'>
                                <Tick3DIcon />
                            </div>
                        ) : (
                            <div className='grid gap-6 mt-4'>
                                {currentDebt.declaration ===
                                    Declaration.User && (
                                    <>
                                        {applicants.length === 2 && (
                                            <SelectBox
                                                labelSize='small'
                                                label='Owner of this debt'
                                                items={formItems.owner.items}
                                                suffix={<ChevronDownIcon />}
                                                name='owner'
                                                validState={
                                                    !!formikProps.values.owner
                                                }
                                                error={getError('owner')}
                                                value={
                                                    formikProps.values.owner ||
                                                    ' '
                                                }
                                                onChange={(
                                                    e: ChangeEvent<HTMLInputElement>,
                                                ) => {
                                                    formikProps.setFieldValue(
                                                        'owner',
                                                        e.currentTarget.value,
                                                    );
                                                }}
                                                onBlur={formikProps.handleBlur}
                                            />
                                        )}
                                        <SelectBox
                                            labelSize='small'
                                            label='Type of debt'
                                            items={formItems.type.items}
                                            suffix={<ChevronDownIcon />}
                                            name='type'
                                            validState={
                                                !!formikProps.values.type
                                            }
                                            error={getError('type')}
                                            value={
                                                formikProps.values.type || ' '
                                            }
                                            onChange={(
                                                e: ChangeEvent<HTMLInputElement>,
                                            ) =>
                                                handleDebtTypeChange(
                                                    e,
                                                    formikProps.setFieldValue,
                                                    formikProps.values.type,
                                                    setCategory,
                                                )
                                            }
                                            onBlur={formikProps.handleBlur}
                                        />
                                    </>
                                )}
                                {showSecurityPropertyQuestion && (
                                    <Controller
                                        label='Is this for your primary residential address?'
                                        labelSize='small'
                                    >
                                        <RadioButtonGroup
                                            name='securityProperty'
                                            value={formikProps.values.securityProperty?.toString()}
                                            error={{
                                                hasError: false,
                                                errorMessage:
                                                    getError(
                                                        'securityProperty',
                                                    ),
                                            }}
                                            onChange={(
                                                e: ChangeEvent<HTMLInputElement>,
                                            ) => {
                                                formikProps.handleChange(e);
                                                formikProps.handleBlur(e);
                                            }}
                                            options={radioButtonOptions}
                                        />
                                    </Controller>
                                )}
                                <CommonQuestions
                                    {...sharedProps}
                                    touched={formikProps.touched}
                                    setTouched={formikProps.setTouched}
                                />

                                {creditCardOrMailOrder && (
                                    <CreditCardMailOrderForm
                                        errors={formikProps.errors}
                                        setTouched={formikProps.setTouched}
                                        touched={formikProps.touched}
                                        {...sharedProps}
                                    />
                                )}
                            </div>
                        )}
                        <Button
                            size='large'
                            color='secondary'
                            className='font-bold w-full mt-10 mb-7 text-lg'
                            type='submit'
                        >
                            <span className='text-lg'>Continue</span>
                        </Button>
                        {!formikProps.values.ignore &&
                            currentDebt.declaration !== Declaration.User && (
                                <div className='flex justify-center'>
                                    <span
                                        onClick={() =>
                                            saveDebt({
                                                ...formikRef.current.values,
                                                dffDebtConSkipped: true,
                                            })
                                        }
                                        className='text-lg cursor-pointer mb-6 lg:mb-0 font-sora-bold'
                                    >
                                        I don't recognise this
                                    </span>
                                </div>
                            )}
                        {error && <HttpError error={error} />}
                    </Form>
                );
            }}
        </Formik>
    );
};

export default DebtInputForm;
